<template>
  <div>
    <div class="box box-info">
      <form>
        <div class="p-fluid p-formgrid p-grid">
          <div class="p-field p-col-12 p-md-4">
            <label for="itemGroups" style="font-size: 1.4rem">Item Category</label>
            <Dropdown
              v-model="selectedGroup"
              :options="itemGroups"
              optionLabel="name"
              placeholder="Select Category"
              class="dropdown-custom"
              @change="filterItemsByGroup"
            />
          </div>
          
          <div class="p-field p-col-12 p-md-6">
            <label for="items" style="font-size: 1.4rem">Items</label>
            <MultiSelect 
              v-model="selectedItems" 
              :options="filteredItems"
              optionLabel="name" 
              placeholder="Select Items"
              :showCheckbox="true" 
              :filter="true"
              @filter="searchItems"
              class="multiselect-custom"
            >
              <template #option="slotProps">
                <div class="p-multiselect-item-option">
                  <span>{{ slotProps.option.name }}</span>
                </div>
              </template>
            </MultiSelect>
          </div>

          <div class="p-field p-col-12 p-md-2 p-mt-5">
            <Button 
              @click.prevent="searchMultiItemProfitData"
              class="p-button-raised" 
              label="Generate Report" 
              icon="pi pi-search"
            />
          </div>
        </div>
      </form>
    </div>
    
    <div class="chart-container">
      <div class="p-grid">
        <div class="p-col-12 p-lg-6 chart-wrapper">
            <h4>Profit/Loss Analysis (Bar)</h4>
            <div class="chart-subcontainer">
              <canvas id="profit-bar-chart"></canvas>
            </div>
          </div>
          
          <div class="p-col-12 p-lg-6 chart-wrapper">
            <h4>Profit/Loss Trends (Line)</h4>
            <div class="chart-subcontainer">
              <canvas id="profit-line-chart"></canvas>
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import MultiSelect from 'primevue/multiselect'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'

export default {
  name: 'MultiItemProfitChart',
  data() {
    return {
      selectedItems: [],
      selectedGroup: null,
      filteredItems: [],
      itemGroups: [],
      allItems: [],
      profitChart: null,
      lineChart: null,
      loading: false
    }
  },
  async mounted() {
    await this.$store.dispatch('loadItemMasterList')
    this.allItems = [...this.itemMasterListByItemType]
    const groups = new Set()
    this.allItems.forEach(item => {
      if (item.groupName) {
        groups.add(item.groupName)
      }
    })
    this.itemGroups = Array.from(groups).map(group => ({
      name: group,
      id: group.toLowerCase().replace(/\s+/g, '-')
    }))
    this.itemGroups.unshift({ name: 'All Items', id: 'all-items' })
    this.selectedGroup = this.itemGroups[0]
    this.filteredItems = this.allItems
  },
  computed: {
    itemMasterListByItemType() {
      return this.$store.state.itemMaster.itemMasterLists
    }
  },
  methods: {
    filterItemsByGroup() {
      if (!this.selectedGroup || this.selectedGroup.id === 'all-items') {
        this.filteredItems = this.allItems
      } else {
        this.filteredItems = this.allItems.filter(
          item => item.groupName === this.selectedGroup.name
        )
      }
      this.selectedItems = []
    },
    async searchMultiItemProfitData() {
      if (this.selectedItems.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please select at least one item!',
          life: 3000
        })
        return
      }
      this.cleanupCharts()
      this.resetCanvases()
      this.loading = true
      try {
        const itemIds = this.selectedItems.map(item => item.id)
        const response = await this.$store.dispatch(
          'loadMultiItemProfitMonthlyReport',
          itemIds
        )
        this.createProfitChart(response)
      } catch (error) {
        console.error('Error loading profit chart:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load profit/loss data',
          life: 3000
        })
      } finally {
        this.loading = false
      }
    },
    cleanupCharts() {
      if (this.profitChart) {
        this.profitChart.destroy()
        this.profitChart = null
      }
      if (this.lineChart) {
        this.lineChart.destroy()
        this.lineChart = null
      }
      if (typeof window.gc === 'function') window.gc()
    },
    resetCanvases() {
      const canvases = ['profit-bar-chart', 'profit-line-chart']
      canvases.forEach(id => {
        const canvas = document.getElementById(id)
        if (canvas) {
          canvas.width = canvas.offsetWidth
          canvas.height = canvas.offsetHeight
          const ctx = canvas.getContext('2d')
          ctx.clearRect(0, 0, canvas.width, canvas.height)
        }
      })
    },
    createProfitChart(chartData) {
      const colorSets = this.selectedItems.map(() => {
        const hue = Math.floor(Math.random() * 360)
        return {
          backgroundColor: `hsla(${hue}, 90%, 60%, 0.7)`,
          borderColor: `hsla(${hue}, 95%, 40%, 1)`,
          pointBackgroundColor: `hsla(${hue}, 100%, 50%, 1)`
        }
      })
      const barCanvas = document.getElementById('profit-bar-chart')
      const barCtx = barCanvas.getContext('2d')
      this.profitChart = new Chart(barCtx, {
        type: 'bar',
        data: {
          labels: chartData.labels,
          datasets: this.selectedItems.map((item, index) => {
            const itemData = chartData.profitData[item.id] || []
            return {
              label: item.name,
              data: itemData,
              backgroundColor: colorSets[index].backgroundColor,
              borderColor: colorSets[index].borderColor,
              borderWidth: 2
            }
          })
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 30,
              bottom: 30,
              left: 20,
              right: 20
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Monthly Profit/Loss Analysis (Bar)',
              font: { size: 20, weight: 'bold' }
            },
            legend: {
              position: 'bottom',
              labels: {
                boxWidth: 15,
                padding: 20,
                font: { size: 14 }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || ''
                  if (label) {
                    label += ': '
                  }
                  const value = context.raw
                  if (value < 0) {
                    label += 'Loss: ' + Math.abs(value).toLocaleString()
                  } else {
                    label += 'Profit: ' + value.toLocaleString()
                  }
                  return label
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              grid: {
                color: '#2c3e50',
                lineWidth: context => context.tick.value === 0 ? 2 : 1,
                alpha: 0.2
              },
              ticks: {
                callback: function(value) {
                  if (value === 0) return '0'
                  if (value < 0) return 'Loss: ' + Math.abs(value).toLocaleString()
                  return 'Profit: ' + value.toLocaleString()
                },
                font: { size: 12 }
              }
            },
            x: {
              grid: { display: false },
              ticks: { font: { size: 12 } }
            }
          }
        }
      })
      const lineCanvas = document.getElementById('profit-line-chart')
      const lineCtx = lineCanvas.getContext('2d')
      this.lineChart = new Chart(lineCtx, {
        type: 'line',
        data: {
          labels: chartData.labels,
          datasets: this.selectedItems.map((item, index) => {
            const itemData = chartData.profitData[item.id] || []
            return {
              label: item.name,
              data: itemData,
              borderColor: colorSets[index].borderColor,
              backgroundColor: 'transparent',
              pointBackgroundColor: colorSets[index].pointBackgroundColor,
              pointRadius: 4,
              tension: 0.2,
              fill: false
            }
          })
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 30,
              bottom: 30,
              left: 20,
              right: 20
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Monthly Profit/Loss Trends (Line)',
              font: { size: 20, weight: 'bold' }
            },
            legend: {
              position: 'bottom',
              labels: {
                boxWidth: 15,
                padding: 20,
                font: { size: 14 }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || ''
                  if (label) {
                    label += ': '
                  }
                  const value = context.raw
                  if (value < 0) {
                    label += 'Loss: ' + Math.abs(value).toLocaleString()
                  } else {
                    label += 'Profit: ' + value.toLocaleString()
                  }
                  return label
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              grid: {
                color: '#2c3e50',
                lineWidth: context => context.tick.value === 0 ? 2 : 1,
                alpha: 0.2
              },
              ticks: {
                callback: function(value) {
                  if (value === 0) return '0'
                  if (value < 0) return 'Loss: ' + Math.abs(value).toLocaleString()
                  return 'Profit: ' + value.toLocaleString()
                },
                font: { size: 12 }
              }
            },
            x: {
              grid: { display: false },
              ticks: { font: { size: 12 } }
            }
          }
        }
      })
    },
    searchItems(event) {
      if (event.filter === undefined) {
        event.filter = ''
      }
      const filterText = event.filter.toLowerCase() || ''
      if (this.selectedGroup && this.selectedGroup.id !== 'all-items') {
        this.filteredItems = this.allItems.filter(item =>
          item.groupName === this.selectedGroup.name &&
          item.name.toLowerCase().includes(filterText)
        )
      } else {
        this.filteredItems = this.allItems.filter(item =>
          item.name.toLowerCase().includes(filterText)
        )
      }
    }
  },
  components: {
    MultiSelect,
    Button,
    Dropdown
  },
  beforeDestroy() {
    this.cleanupCharts()
  }
}
</script>

<style scoped>
.multiselect-custom, .dropdown-custom {
  font-size: 1.4rem;
  min-height: 35px;
}

.item-name {
  font-size: 1.3rem;
  margin-left: 8px;
}

::v-deep .p-multiselect-label, 
::v-deep .p-dropdown-label {
  font-size: 1.3rem !important;
  padding: 0.75rem !important;
}

::v-deep .p-multiselect-item,
::v-deep .p-dropdown-item {
  font-size: 1.3rem !important;
  padding: 0.75rem 1.25rem !important;
}

.chart-container {
  margin-top: 2rem;
  padding: 1rem;
}

.chart-wrapper {
  margin-bottom: 2rem;
}

.chart-subcontainer {
  height: 500px;
  margin: 1rem 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  padding: 1.5rem;
}

h4 {
  color: #444444;
  margin: 1.5rem 0;
  font-size: 1.8rem;
  border-bottom: 2px solid #dddddd;
  padding-bottom: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// ItemWiseProfitLossAnalysisGraph.vue?6189d216